import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type Endcap } from '@/features/shop/services/Search/types'
import { useEffect } from 'react'
import {
  type PromoCardEvent,
  type PromoCardProps,
  trackPromoCardClicked,
  trackPromoCardViewed,
} from '@/features/shared/analytics/promoCard'
import { parseUrl } from '@/features/shared/utils/queryString'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { type ParsedUrlQuery } from 'node:querystring'
import { trackSponsoredContentLoaded } from '@/features/shared/analytics/promotions'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { EndcapV2 } from '@/features/shop/components/ProductEndcap/EndcapV2'

type Props = {
  endcap: Endcap
  gridIndex: number
}

interface EndcapPromoCardProps extends PromoCardProps {
  endcap: Endcap
  searchParams: ParsedUrlQuery
}

const getEndcapPromoCardProperties = ({
  endcap,
  searchParams,
  gridIndex,
}: EndcapPromoCardProps): PromoCardEvent => {
  const clickUri = parseUrl(endcap.click_uri)
  const promotionCategoryId = clickUri.query.promotion_category_id
  return {
    content: searchParams.query ? String(searchParams.query) : undefined,
    creative: endcap.image_background_url,
    display_format: DISPLAY_FORMAT.GRID,
    display_sublocation: 'banner endcap',
    featured_transaction_id: endcap.featured_transaction_id,
    grid_index: gridIndex,
    location: urlPathnameToLocationName(),
    promotion_category_id: promotionCategoryId ? String(promotionCategoryId) : undefined,
    promotion_type_name: 'endcap',
    next_location: endcap.click_uri,
    source: endcap.source,
    search_term: String(searchParams.query ?? ''),
  }
}

export const ProductEndcap = ({ gridIndex, endcap }: Props) => {
  const searchParams = useSearchQueryParams()
  const { beacons, featured_transaction_id } = endcap
  const { on_load_beacon, on_click_beacon, on_view_beacon } = beacons ?? {}

  useEffect(() => {
    if (on_load_beacon) {
      trackSponsoredContentLoaded({
        beaconUrl: on_load_beacon,
        featuredTransactionID: featured_transaction_id ?? '',
      })
    }
  }, [on_load_beacon, featured_transaction_id])

  const handleInView = () => {
    trackPromoCardViewed({
      ...getEndcapPromoCardProperties({
        endcap,
        searchParams,
        gridIndex,
      }),
      beacon_url: on_view_beacon,
    })
  }

  const handleClick = () => {
    trackPromoCardClicked(
      {
        ...getEndcapPromoCardProperties({
          endcap,
          searchParams,
          gridIndex,
        }),
        beacon_url: on_click_beacon,
      },
      { sendImmediately: true },
    )
  }

  return (
    <ScrollIntoView onInView={handleInView}>
      <EndcapV2 endcap={endcap} onClick={handleClick} />
    </ScrollIntoView>
  )
}
